import React, { useState } from "react";
import { Helmet } from "react-helmet";

import {
    Form,
    Group,
    Input,
    Select,
    Heading,
    LoadingIcon,
} from "@peracto/peracto-ui";
import { useConfig } from "@peracto/peracto-config";
import axios from "axios";

import * as yup from "yup";
import { toast } from "react-toastify";

import { CodeFormatter } from "components/CodeFormatter";
import { CopyButton } from "components/CopyButton";

const schema = yup.object().shape({
    country: yup.string().required(),
    productSku: yup.string().required(),
    supplierId: yup.string().required(),
});

export const PartDetailsForm = () => {
    const config = useConfig();
    const API_URL = config.get("api");

    const [loading, setLoading] = useState(false);
    const [requestData, setRequestData] = useState("");
    const [responseData, setResponseData] = useState("");

    const onSubmitForm = async (formData, actions) => {
        setLoading(true);

        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
        };

        try {
            const { data } = await axios.post(
                `${API_URL}/test-harness/raindata-get-part-details`,
                formData,
                {
                    headers,
                }
            );

            setRequestData(data?.request);
            setResponseData(data?.response);
        } catch (e) {
            console.error(e);

            toast.error(
                e?.error?.body?.hasOwnProperty("hydra:description")
                    ? e.error.body["hydra:description"]
                    : "There was an error submitting this form."
            );
        } finally {
            setLoading(false);
            actions.setSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Part Details | Raindata | Web Services | Peracto</title>
            </Helmet>
            <div className="form-container">
                <Heading name="Part Details" />

                <Form
                    autoComplete="on"
                    values={{
                        country: "GB",
                        supplierId: "1973",
                        productSku: "",
                    }}
                    schema={schema}
                    showActionPanel={false}
                    onSubmit={onSubmitForm}
                >
                    <Group
                        key="part-details"
                        id="part-details"
                        name="Part Details"
                    >
                        <Select
                            name="country"
                            label="Trading Country"
                            placeholder="Please select..."
                            options={[
                                {
                                    label: "United Kingdom",
                                    value: "GB",
                                },
                                { label: "Ireland", value: "IE" },
                            ]}
                            testId="tradingCountry"
                            required={true}
                        />

                        <Input
                            name="productSku"
                            label="ECP Part Number"
                            testId="ecpPartNumbner"
                            help="Example: 10111096"
                            required
                        />

                        <Input
                            name="supplierId"
                            label="Supplier ID"
                            testId="supplierId"
                            help="Product Specific Supplier ID. Default is: 1973"
                            required
                        />

                        <button
                            className="btn btn-primary"
                            type="submit"
                            data-testid="submit"
                        >
                            Submit
                        </button>
                    </Group>

                    {loading && (
                        <div className="flex justify-center items-center py-5">
                            <LoadingIcon />
                        </div>
                    )}

                    {requestData?.length > 0 && !loading && (
                        <Group
                            key="request-details"
                            id="request-details"
                            name="Request Details"
                        >
                            <div className="flex items-center justify-between mt-2">
                                <h4 className="text-[18px] font-semibold mr-2 mb-0">
                                    Raindata Request
                                </h4>{" "}
                                <div className="flex flex-row">
                                    <CopyButton content={requestData} />
                                </div>
                            </div>

                            <div className="border rounded-[4px] p-2  mt-3">
                                <CodeFormatter
                                    code={requestData}
                                    language="json"
                                />
                            </div>
                        </Group>
                    )}

                    {responseData?.length > 0 && !loading && (
                        <Group
                            key="response-details"
                            id="response-details"
                            name="Response Details"
                        >
                            <div className="flex items-center justify-between mt-2">
                                <h4 className="text-[18px] font-semibold mr-2 mb-0">
                                    Raindata Response
                                </h4>{" "}
                                <div className="flex flex-row">
                                    <CopyButton content={responseData} />
                                </div>
                            </div>

                            <div className="border rounded-[4px] p-2 mt-3">
                                <CodeFormatter
                                    code={responseData}
                                    language="json"
                                />
                            </div>
                        </Group>
                    )}
                </Form>
            </div>
        </>
    );
};

PartDetailsForm.displayName = "PartDetailsForm";
